<template>
<div class="chapter">
<div class="inner_page">
<h2 class="calibre5">Zur Einführung</h2>
<p class="calibre3">Der Bauer fängt heute an, eine immer größere Rolle im
öffentlichen Leben Polens zu spielen, die im Gegensatz zu der
verhältnismäßig viel unbedeutenderen, der ländlichen bäuerlichen
Bevölkerung bei den stark industrialisierten westeuropäischen
Völkern auch noch die Tendenz hat zu einer maßgebenden Macht wie im
politischen, so auch im geistigen Leben zu erstarken. Der Bauer ist
gegenwärtig in Polen nicht nur Mode, er muß auch als eine
aufwachsende neue Möglichkeit, als wieder zum erstenmal nach vielen
Jahrhunderten urbar gewordenes Land betrachtet und eingeschätzt
werden. Das gegenwärtige polnische öffentliche Leben, die moderne
polnische Wissenschaft, Technik, Kunst und Literatur haben eine
ganze Reihe hervorragender Männer aufzuweisen, die unmittelbar aus
dem Bauernvolke hervorgegangen sind. Es mögen hier beispielsweise
einige Namen von Bedeutung auf dem Gebiete der polnischen Dichtung
folgen, die alle mit dem Bauerntum unmittelbar zusammenhängen; ich
nenne Jan Kasprowicz, Stanislaw Przybyszewski, Wladislaw Stanislaw
Reymont, den Verfasser des vorliegenden großen Bauernepos, Ladislaw
Orkan, Josef Jedlicz, A. Gwidz, ohne die Allerjüngsten, noch
Ringenden, zu berücksichtigen, unter denen auch eine Anzahl von
Dichtern zu finden ist, die zugleich schreiben und ihr Bauernland
beackern/ eine Verschmelzung zweier verschiedenartiger
Betätigungen, die uns im Westen Europas als ein geträumtes Idyll
oder eine Schäferpose anmuten könnte, und dennoch keines von beiden
ist. In dieses Kapitel gehören auch die seinerzeit so
sensationellen Bauernheiraten dreier namhafter polnischer Künstler:
des Malers Wladimir Tetmajer und der beiden Dichter Lucian Rydel
und Stanislaw Wyspianski, die sich, eine uralte polnische Tradition
auffrischend, Bauerntöchter, barfüßige, frische Mädchen in <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page5" title="JohannN/lac"></a> leuchtenden, samtenen
Bauernmiedern und buntgestreiften Beiderwandröcken zu Frauen
genommen haben. Tetmajer und Rydel zogen aufs Land und sind somit
durch ihre Heirat in ein besonders intimes Verhältnis zum
Bauernstand getreten, dessen Interessen Tetmajer heute mit anderen
Männern aus dem Volke im österreichischen Reichstag vertritt.</p>
<p class="calibre3">Ein besonderes Denkmal dieser Verbindungen ist das in Polen
berühmt gewordene Märchendrama »Hochzeit«, das der früh verstorbene
Dichter, Maler, Architekt und Plastiker Stanislaw Wyspianski, eine
polyphone Renaissancenatur und eines der bedeutendsten und
stärksten Talente, die das moderne Polen aufzuweisen hat, verfaßte.
In dieser rhythmisch und gedanklich bestrickenden szenischen
Dichtung finden wir das ganze seelische Verhältnis des modernen
Polentums zum Bauernstand mit einer Tiefe und Macht begriffen,
erfüllt und gestaltet, daß man das Werk als wichtigsten Beitrag zur
Vertiefung dieser Frage bezeichnen kann.</p>
<p class="calibre3">Der Bauer hat natürlich auch viel früher schon eine gewisse
Rolle in der polnischen Literatur gespielt; schon ganz am Anfang
des 19. Jahrhunderts finden wir ihn in der allerdings noch recht
sentimentalen, auf Rousseausche Naturschwärmerei zurückzuführenden
Dichtung Brodzinskis »Wieslaw«, die von den Zeitgenossen mit
Begeisterung aufgenommen wurde. Die Romantiker bringen neben dem
Volkslied eine Menge Volksmotive in die Dichtung und knüpfen aufs
neue das lange zerrissen gewesene Band zwischen hoch und niedrig,
doch ist ihre idealisierte Darstellung des Volkes noch weit davon
entfernt, eine Gestaltung des Lebens zu sein. Um überhaupt die ganz
eigene Stellung des Bauern im polnischen Volksorganismus zu
begreifen, wird es unerläßlich sein, einen Blick in historische
Zusammenhänge zu tun.</p>
<p class="calibre3">Die Geschichte des polnischen Bauerntums bis zum Fall <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page6" title="JohannN/lac"></a> Polens umfaßt 5
Entwicklungsstufen/eine Einteilung, die auch mehrere
Geschichtschreiber angenommen haben. Die erste umspannt den
Zeitraum von den frühesten Zeiten bis zur Einführung des
Christentums und zur Belehnung der katholischen Geistlichkeit mit
Gütern, reicht also ungefähr bis zur Mitte des 10. Jahrhunderts.
Sie ist vor allem dadurch interessant, daß sie besonders wichtige
Aufschlüsse über das Wesen des polnischen Volkes gibt und in enger
Beziehung zu späteren nationalen Reformen sowie zum politischen
Programm des heutigen Bauerntums steht. Die ersten maßgebenden
Schilderungen der polnischen Dorfgemeinden finden wir bei Herodot,
Julius Cäsar und einigen polnischen Chronisten. Die Schilderungen,
die Julius Cäsar von den Einrichtungen der Sueven macht, lassen
sich unverändert auf die Slawen übertragen. Er berichtet über eine
Einteilung des Landes in 100 Kreise, die von Freien und Unfreien
bewohnt waren. Die ersten waren die eigentlichen Bürger (
<span class="teletype">ingenui</span>), deren einem Teil das
Handwerk des Krieges außerhalb der Landesgrenzen oblag, während der
andere seinen Acker bestellte, um im folgenden Jahre die
heimkehrenden Krieger abzulösen. Der Ackerbau wurde mit Hilfe der
Unfreien ( <span class="teletype">servi</span>), die aus
Kriegsgefangenen vervollständigt wurden, betrieben.</p>
<p class="calibre3">Die soziale Einrichtung der betreffenden Gemeinden trägt einen
ausgesprochenen demokratisch-kommunistischen Charakter. Der Boden
ist nicht Besitz von einzelnen, sondern gehört den Gemeinden, die
sich selbständig regieren und dem Einzelnen das Land zur
Bearbeitung auf eine gewisse Zeit austeilen, um, nachdem die
Ergiebigkeit des Bodens nachgelassen hat, weiterzuziehen. Diese
Einrichtung, die übrigens in Mitteleuropa eine Zeitlang überall
bestand, ist ein Gegensatz der in skandinavischen Ländern
entstandenen aristokratischen Höfe. Während bei den kommunistischen
Gemeinden ein Zuwachs an Mitgliedern eine Steigerung der Macht der
Gemeinde bedeutete, da sie um <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page7" title="JohannN/lac"></a> so mehr herrenloses Land an sich reißen und
urbar machen konnte, war für den Ackerbau im Norden, dem nicht
soviel fruchtbares Land zur Verfügung stand, jeder hinzukommende
Fremdling ein Feind und Schmäler seiner Einkünfte. Man schloß sich
lieber an den stärkeren Nachbar an, bei dem man Aussicht hatte,
eine kräftige Unterstützung seiner persönlichen Interessen und
Rechte im Falle der Not zu erwarten. Es entstehen hier also von
vornherein Vasallen- und Freundschaftsbündnisse, die sich
unmittelbar auf eine persönliche Wertung, also auf ein
individualistisch aristokratisches Prinzip gründen. Auf
ausschließlich äußerliche Bedingungen, wie Bodenbeschaffenheit,
Klima u. dgl., lassen sich diese Gegensätze zwischen Dorfgemeinde
und Hof jedoch nicht zurückführen. Wie sehr hier das allmächtige
Blut und die Instinkte der Rasse als gestaltende Mächte eingreifen,
beweist die Geschichte zur Genüge. Ich möchte nur zunächst auf die
eigentümliche Erscheinung hinweisen, daß die kommunistische
Dorfgemeinde, die ja übrigens in den ersten Jahrhunderten unserer
Zeitrechnung nicht nur bei den Slawen, sondern in ähnlichem Maße
bei den Teutonen, Sueven, Sachsen, Dalmatiern u. v. a. zu finden
ist, doch vornehmlich bei den Slawen, und das selbst gewissermaßen
bis auf den heutigen Tag Geltung behält, während die germanischen
Völker sehr bald zu einer aristokratischen politischen Einrichtung
gelangen. Die Grundlage dazu finden wir in der »Lex Salica« mit
ihrem fest umrissenen Erbrecht.</p>
<p class="calibre3">Die Höfe erscheinen bei den Polen erst nach der Einwanderung der
»Lechen«, die, wie in der russischen Ebene die herbeigerufenen
»Russen«, das aristokratische Prinzip in die kommunistische
slawische Gemeinde hineintragen, ohne sie jedoch von Grund aus
umgestalten zu können. Mit den Lechen, die im 6. Jahrhundert nach
Christi Geburt in Polen einzudringen begannen/man kann sie mit
ziemlicher Sicherheit als die normannischen Eroberer
bezeichnen/erscheint <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page8" title="JohannN/lac"></a> auch unter den Polen zum erstenmal der Begriff
des Edelmanns /Schlachziz/während bis dahin das Land nur von
Hüfnern ( <span class="teletype">Cmetones</span>, wie die Chronik
sie nennt, den polnischen Namen: Kmieti, Kmeten latinisierend) und
Bauern bewohnt wurde. Die Kmeten waren jene ackerbauenden Krieger (
<span class="teletype">ingenui</span>) und die Bauern jene
<span class="teletype">servi</span>, von denen bei Julius Cäsar die
Rede ist. Auch die ersten vorgeschichtlichen Fürsten, von denen die
Sage dunkel raunt, Krakus, der Gründer von Krakau, und Popiel, der
Fürst von Gnesen, sind Lechen. Wichtig und bezeichnend allerdings
ist, daß zum Gründer der ersten polnischen Königsdynastie kein
Lech, sondern der Kmet Piast wird, von dem der polnische Chronist
Gallus sagt, daß er beim Lechen Popiel, der in Gnesen regierte,
<span class="teletype">»arator«</span> <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" href="#/Text/#foot1" id="text1"><sup class="calibre6">F1</sup></a> war und aus bäuerlichem Stande kam.</p>
<p class="calibre3">Die polnische Dorfgemeinde wird nun allmählich aus einer
kommunistischen eine aristokratisch-republikanische. Der neben dem
Dorf bestehende Herrenhof sammelt um sich die Wohnstätten der
Gutsknechte und erlaubt auch für entsprechende Gegenleistungen
Personen des Bauernstandes, sich auf seinem Grund und Boden
anzusiedeln. Allerdings befindet sich nur der Acker in festen
Händen, während alles Brachland, Triften, Weideplätze und der Wald
gemeinsamer Besitz bleiben.</p>
<p class="calibre3">Wir werden in der vorliegenden Reymontschen Schilderung des
heutigen Bauernlebens genau den bis in diese Uranfänge reichenden
Fäden begegnen können, sie bilden eines der wichtigsten Motive im
sozialen Gemälde des polnischen Dorfes, das er entwirft.</p>
<p class="calibre3">Das Christentum bringt den Slawen die neuen Begriffe einer
monarchisch-aristokratischen Rangordnung. Gerade der Gegensatz
davon, was Nietzsche als den Aufstand der Herdeninstinkte
schildert, tritt unter seinem Einfluß in polnischen Ländern ein.
Soweit hat sich das Christentum schon <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page9" title="Wunibald/lac"></a> unter römischem und germanischem Einfluß
gewandelt. Man wird, glaube ich, überhaupt erst auf dieser
Grundlage die treue Anhänglichkeit der Polen für das päpstliche Rom
begreifen. Sie ist ein aristokratischer Zug und paßt zu den
politischen Anschauungen des immer mehr ausschlaggebenden
Adelsstandes, der polnischen »Schlachta«. Von Wichtigkeit ist der
Einfluß der römisch-katholischen Geistlichkeit auf die Lage des
polnischen Bauernstandes. Wir finden auf den Gütern der höheren
Geistlichkeit, sowie auf denen des Königs (diese Würde ist ein
westlicher, mit dem Christentum bei den Slawen eingeführter
Begriff) und einzelner höherer Würdenträger freie Landleute und
Unfreie, die Abgaben zahlen. Wer keinen Grundbesitz hatte, konnte
Land gegen Abgaben erhalten/eine Sitte, die sich immer mehr
einzubürgern beginnt, seitdem sich die Kmeten (Hüfner) fast
ausschließlich dem Kriegshandwerk und der Verwaltung der Ämter
widmen. Die Abgaben Zahlenden waren entweder nur durch ihre
freiwillig zu vereinbarenden Zahlungen, die in Geld oder
Erzeugnissen des Ackerbaus bestanden, dem Besitzer verbunden, oder
sie waren unfreie Bauern und waren verpflichtet, zu tun, was man
ihnen befahl. Die Abgaben zahlende Landbevölkerung war somit frei
und hatte das Recht, sich aus ihrem Wohnsitz jederzeit nach
Erfüllung ihrer Verpflichtungen zu entfernen. Daß die Lage dieses
Bevölkerungsteiles in Polen günstig gewesen sein muß, beweist die
beginnende starke Einwanderung der Deutschen, deren Einfluß auf das
Werden des polnischen Staates sich auch anderweitig und vor allem
durch kirchliche Angelegenheiten/die polnische Kirche war eine
Zeitlang von dem Erzbistum in Magdeburg abhängig/ bemerkbar macht.
<a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" href="#/Text/#foot2" id="text2"><sup class="calibre6">F2</sup></a></p>
<p class="calibre3">Auf diese Weise trägt das Christentum eine neue, auf <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page10" title="Johnboy2001/divisi"></a>
monarchischem Prinzip gegründete politische Ordnung nach Polen
hinein, in der die Macht der Geistlichkeit und der Herren zu einem
wichtigen gesellschaftlichen und volkswirtschaftlichen Faktor wird.
Daß diese neuen Ordnungen zunächst einen Volksaufstand
hervorgerufen hatte, beweist, wie fremd sie eigentlich den
Einrichtungen des Landes waren.</p>
<p class="calibre3">Die dritte Epoche ist dadurch wichtig und interessant, daß sie
zeigt, in welcher Weise das polnische Volk mit den ihm
aufgepfropften fremden politischen Formen fertig geworden ist,
indem es sie seinem Charakter entsprechend ummodelte. Ich möchte
hier im voraus schon betonen, daß die heutigen Begriffe und
Reformpläne, der auf die Kraft des Bauerntums bauenden
allpolnischen politischen Elemente gerade an diese Epoche des
politischen Lebens und ihre Ideale anknüpfen. Wie zugänglich der
polnische Bauer für diese Tradition auch heute noch ist, beweist
der Reymontsche Roman recht unzweideutig. Besonders die Propaganda
des alten Bettlers Rochus ist dafür bezeichnend.</p>
<p class="calibre3">Die unter dem bedeutendsten Herrscher aus der Piastendynastie
Kasimir dem Großen herausgegebene Gesetzsammlung von Wislica und
die späteren Verordnungen der Könige der Jagellonischen Dynastie
geben den übernommenen aristokratisch-monarchischen Einrichtungen
einen immer stärker hervortretenden demokratisch-republikanischen
Zug. Um diese Zeit entstehen neben der ursprünglichen, sich selbst
regierenden Dorfgemeinde mit gemeinsamen Triften und Wäldern noch
zwei andre: die aus Adel und Landleuten bestehende Dorfgemeinde mit
polnischer Gerichtsbarkeit und die aus Adel und Landleuten
bestehende Gemeinde, in der das Magdeburgische Recht galt. Uns
interessiert hier vor allem diese zweite Gemeinde, da sie ein
besonderes Licht auf den unter deutscher Bevölkerung zu jener Zeit
beginnenden Drang nach Osten wirft, der gewissermaßen ein
Gegenstück zu dem, wenn auch aus <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page11" title="lac/woldemar"></a> ganz anderen Gründen erwachten, aber in
seinen rassegeschichtlichen Folgen nicht minder schwerwiegenden
Drang nach Westen unter den Massen des slawischen Proletariats
genannt werden könnte. Auch diese Frage ist noch heute aktuell, und
auch sie kommt im Reymontschen Roman zur Geltung.</p>
<p class="calibre3">Die erwähnte, dem Magdeburgischen Recht unterstehende
Dorfgemeinde hat sich in Polen unter folgenden Bedingungen
gebildet. Das Auftauchen der Tatarenhorden im Osten Europas und die
Entvölkerung des Landes durch die blutigen Kämpfe mit ihnen
erwecken im polnischen Reich das Bedürfnis nach frischen
Arbeitskräften. Die Einwanderung der Deutschen wird begünstigt und
die Geistlichkeit ist es, die zuerst die Erlaubnis erwirkt (die
Zisterzienser in Lubeni 1175), deutsche Kolonisten auf ihren Gütern
anzusiedeln. Diese deutschen, damals gern gesehenen Kolonisten
haben auf die Einrichtungen der polnischen Dorfgemeinde insofern
eingewirkt, daß sie einen neuen Typus, den des adelig-bäuerlichen
Dorfes schufen. Auch das Entstehen gewisser Dorfbeamten: des
Schulzen und Schultheißen, ist auf deutschen Einfluß
zurückzuführen.</p>
<p class="calibre3">In diesem Zustand findet die Gesetzsammlung von Wislica die
polnischen Dorfgemeinden. Die Hauptaufgabe, die Kasimir der Große,
der Bauernkönig, wie ihn das polnische Volk auch heute noch nennt,
in seiner Gesetzgebung verfolgte, war möglichst weitgehende Rechte
für den Bauernstand, als den Nährstand des Landes, zu sichern,
darum verschließt er auch in seinen Bestimmungen den Kmeten nicht
die Wege zu höheren Ämtern und versucht, sie möglichst dem
Adelsstand gleichzustellen. Auch die Entrichtung der Vergütungen
für Nutznießung des Bodens beruht gänzlich auf freiwilliger
Abmachung/ein Zustand, der ziemlich weit von dem der
Leibeigenschaft bezeichnet werden kann und den noch die durchaus
nicht streng zu nennenden Bestimmungen über die Auswanderung zu
<a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page12" title="JohannN/woldemar"></a> einem
Zustand ziemlicher Bewegungsfreiheit stempeln. Wichtig ist außerdem
noch die Erhebung der Schulzen und Schultheißen in den Ritterstand
dritten Grades und die Unterwerfung aller Untertanen, ob sie Bürger
oder Bauern, Juden oder Edelleute sind, unter ein und dasselbe
Recht. Darin zeigt sich die Gesetzsammlung von Wislica als ein
unzweideutiger Ausdruck echt slawischer demokratischer Gesinnung.
Der soziale Aufbau des polnischen Volkes ist diesen
Gesetzbestimmungen gemäß ein demokratisch betonter und weist
durchaus keine Kluft zwischen oben und unten auf. Heiraten zwischen
Adeligen und Bauerntöchtern, und umgekehrt, sind um diese Zeit
keine Seltenheit, besonders sind es die Schulzen und Schultheißen,
die eine Zeitlang (bis 1496) eine dem Adel fast an Ansehen
gleichkommende Stellung einnahmen und zu höheren Ämtern zugelassen
wurden.</p>
<p class="calibre3">Mit dem Eindringen der Adelsbegriffe von Westen, und vorzüglich
ist es das nahe deutsche Beispiel, das immer wieder verlockend
wirkt, versucht der polnische Adel besondere Rechte für sich zu
erwirken, was ihm allmählich auch gelingt. Die Einzelheiten dieser
Wandlung des noch unter den Jagellonen demokratisch-republikanisch
betonten polnischen Staates zu einer der Willkür der Magnaten
preisgegebenen Adelsrepublik zu schildern, würde zu weit führen;
für unsere Untersuchung ist nur wichtig, daß der polnische
Bauernstand während dieses Zeitraums in völlige Leibeigenschaft
(nach den Reichstagsbestimmungen vom Jahre 1520) gerät. Von nun an
beginnt eine rein adelige polnische Geschichte, ein vom
demokratisch-kommunistischen Unterton des slawischen
Rassecharakters losgelöstes Intermezzo, das mit dem schrillen
Mißton eines völligen Zusammenbruchs abreißt.</p>
<p class="calibre3">Der polnische Bauer war jahrhundertelang national totes Land,
zum erstenmal tritt er wieder hell ins Licht der Geschichte nach
dem Fall Polens, als der Leiter des polnischen <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page13" title="JohannN/woldemar"></a> Aufstandes v. J. 1830, der
General Kosciuszko die Bauern unter seine Fahnen berief. Man hatte
allerdings schon vor Polens Fall Anstrengungen gemacht, den
Bauernstand wieder aus dem Zustand seiner Rechtlosigkeit zu heben,
die ersten Versuche datieren selbst ins Jahr 1733 zurück, doch
wirklich durchgreifende Reformen sind nicht erfolgt, und selbst der
großzügige Entschluß des letzten polnischen Reichstags wurde durch
den Eigennutz der Großen im Reiche zuschanden gemacht. Der
polnische Bauer, der bereits in den napoleonischen Legionen zu
finden ist und in der großen Armee des wider Rußland ziehenden
Korsen gegen Moskau marschiert, wird eigentlich erst im Aufstand v.
J. 1830 sichtbar. Das Bild der polnischen Sensenmänner erscheint
auf der Bildfläche des nationalen Bewußtseins, sie tragen das rote
Banner mit einer goldgestickten Garbe darauf / ein
zusammenfassendes Sinnbild des polnischen Bauernvolkes: das
demokratische Rot und das erdgebundene Ährengold, auf dem stolz und
selbstbewußt, als ob sie eine ganze lange Vergangenheit wegwischen
wollten, die Worte leuchten: »Sie nähren und wehren.« Allerdings
haben die polnischen Bauern das Vaterland nicht retten können, denn
erstens hatte sich nur ein kleiner Bruchteil, die Elite des
Bauerntums unter den Fahnen Kosciuszkos versammelt und zweitens
auch einzig solche, die der auf seine kleinlich-egoistischen
Interessen im allgemeinen bedachte Adel ins Feld ziehen ließ. Nach
diesem Aufflammen, das nicht ohne Heroismus ist, muß der Bauer
wieder in seine alten unerträglichen sozialen Verhältnisse zurück,
er ist nach wie vor rechtlos, auf Gnade und Ungnade der Knute
seines Herrn ausgeliefert, und die Last der Fronarbeit drückt
ungemindert auf seine tief gebeugten Schultern. Daß unter solchen
Umständen von einem politischen Erwachen des Bauern nicht die Rede
sein kann, liegt auf der Hand. Ja, während allmählich das polnische
Dorf literaturfähig wird (als erster tut es der vielseitige,
außerordentlich fruchtbare Schriftsteller J. J. Kraszewski <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page14" title="JohannN/woldemar"></a> ) und man für
ländliche Motive zu schwärmen beginnt, indem man sich zum Volke
»niederbeugt«, lernt der Bauer am eigenen Leibe nichts als die
ganze Last seiner Rechtlosigkeit kennen. Allerdings war der einmal
gefaßte Gedanke der Befreiung des Volkes aus der Leibeigenschaft,
als dessen Ausdruck der letzte polnische Reichstag und sein
Manifest vom 3. Mai zu betrachten sind, ununterbrochen im Laufe der
darauf folgenden über 60 Jahre an der Arbeit gewesen, doch hat er
eine positive gesetzgebende Tat nicht vollbracht. So wurde es für
lange Jahre zum Verhängnis für die innere polnische Einheit, daß
die eigentlichen Befreier des polnischen Bauernvolkes aus der
Leibeigenschaft nicht die polnischen regierenden Stände, sondern
erst die fremden Regierungen wurden, die das geteilte Polen
politisch zu verwalten begonnen hatten. Außerdem wurden durch die
verschiedenen Maßnahmen einer jeden der drei Regierungen bei der
Befreiung des polnischen Bauers aus der Leibeigenschaft ganz
verschiedene ökonomische Verhältnisse für das polnische Bauernvolk
eines jeden Teiles geschaffen, so daß man von nun an von den
polnischen Bauern in Deutsch-Polen, Österreichisch-Polen und
Russisch-Polen, als von drei sich verschieden entwickelnden
volkswirtschaftlichen Gruppen reden muß.</p>
<p class="calibre3">Das Mißtrauen und die feindselige Stimmung des polnischen Dorfes
gegenüber der polnischen Intelligenz und besonders gegenüber dem
polnischen Adel kommt auch noch hier und da im Reymontschen
Bauernroman, besonders in dem vierten Teil seines Werkes zutage, wo
die politischen und sozialen Zusammenhänge des Dorfes am
sichtbarsten hervortreten. Bezeichnend dafür sind die Worte, die
Reymont einem alten Bauer auf einer Gemeindeversammlung in den Mund
legt:</p>
<p class="calibre3">»Lacht nur zu, aber ich kann es euch sagen, wie es um jene
Zeiten herum aussah, als die Herren ihren Aufstand <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page15" title="JohannN/woldemar"></a> gemacht haben; ich weiß
noch gut, wie sie uns vorgeschwindelt und geschworen haben, daß sie
uns, wenn Polen wieder zurechtkäme, die Freiheit und Wald und Land
und was nicht noch alles geben würden! Vorgeredet und versprochen
haben sie es uns, und was wir gekriegt haben, das hat uns ein
anderer gegeben, und bestrafen mußte er sie noch, weil sie in
keiner Sache dem Volk eine Erleichterung schaffen wollten. Aber
mich lockt keiner auf Spreu, ich weiß gut, was dieses, ihr Polen,
bedeuten soll: das ist nur eine Peitsche für unseren Buckel und
Knechtschaft und Leibeigenschaft!« ...</p>
<p class="calibre3">Diese feindliche Voreingenommenheit, die man noch um die 80er
und selbst noch 90er Jahre des neunzehnten Jahrhunderts im
polnischen Dorf allgemein finden konnte, hat allmählich eine
Wandlung erfahren. Nach der Zeit des herablassenden Interesses für
das Dorf kam die Zeit des Mitgefühls mit dem sozialen Elend; man
dichtete und besang den »Bruder im rauhen Bauernkittel« und fand
auch hin und wieder echte, tiefe Töne. Vor allem sind es einzelne
Frauen, wie die Dichterin Maria Konopnicka, Polens Selma Lagerlöf,
und Elisa Orzeszkowa, auch Henryk Sienkiewicz schreibt um diese
Zeit seine Dorfnovellen. Doch es überwiegt noch immer eine
oberflächliche Sentimentalität, die einen besonders großen Zuspruch
der Menge findet.</p>
<p class="calibre3">Erst die um 1887 gegründete Wochenschrift »Glos« (Die Stimme),
die um sich alle wirklich schöpferischen demokratischen Kräfte des
modernen Polens sammelt, leitet einen neuen Kurs in den Beziehungen
der polnischen Intelligenz zum Volke ein. Die Erkenntnis dämmert
auf, daß man das Volk nicht anders als durch das Volk selbst für
die polnische Sache gewinnen konnte. Es beginnt die Zeit der
schweren, oft mit den größten Gefahren und Entbehrungen verbundenen
Arbeit im Volke. Der Bettler-Apostel Rochus im Reymontschen Roman
ist ein Beispiel dafür. Geheimverbindungen, die selbst mitten unter
die lernende Schuljugend <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page16" title="Ruktz/woldemar"></a> reichen, spannen ein vielmaschiges Netz über
das ganze Land aus. An der Spitze aller Bestrebungen steht /
Volksaufklärung! Schritt für Schritt versucht man vorzudringen,
ungeachtet der vielen Opfer, die besonders in Russisch-Polen diese
Arbeit fordert, da die russische Regierung alle Bemühungen dieser
Art aufs schärfste bestraft. Nächtliche Haussuchungen,
Verhaftungen, Deportation auf sog. administrativem Wege, d. h. ohne
Gericht, jahrelanges Schmachten in Gefängnissen und die Schrecken
des sibirischen Exils sind an der Tagesordnung. Jetzt setzt auch
eine ganz neue Phase in den Beziehungen zum Dorf ein. Man entdeckt
abermals den polnischen Bauer, aber jetzt nicht mehr als einen
Gegenstand des Interesses oder des Mitleids, sondern als eine
Quelle der Kraft. Unter den Werken, die das zuerst tun, ist das des
Malers Stanislaw Witkiewicz über die galizischen Tatragebirgler das
grundlegende. Die Tatralandschaft und der Tatrastil / der erste in
Polen zum Ansehen gelangte Bauernstil / werden hochaktuell. Die
Tatralandschaft fand ihren Epiker in Kasimir Tetmajer, dem Bruder
des Malers Wladimir.</p>
<p class="calibre3">Die Linie von dieser neuen Entdeckung des polnischen Bauers bis
zu den politischen Idealen der Neuzeit, die den Bauer in den
Mittelpunkt jeglicher polnischer Probleme und Hoffnung stellen und
besonders in der Partei der polnischen National-Demokratie mit
ihren allpolnischen Bestrebungen einen politisch greifbaren
Ausdruck finden, ist klar genug sichtbar. Er ist, wenn man die
Richtung näher charakterisieren soll, ein Anknüpfen an altpolnische
bäuerliche, dem Grundcharakter nach demokratische Überlieferungen,
ein Überspringen des ganzen adeligen Intermezzos und ein
Beiseiteschieben des bürgerlichen Mittelstandes, der in Polen
sowieso niemals eine Rolle zu spielen vermocht hatte / kurzum, eine
vollständige Änderung des Kurses. Der polnische Bauer mit seinen
Zukunftsmöglichkeiten ist der leitende Stern der realen und idealen
Politik. <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page17" title="Ruktz/lac"></a> Mit
anderen Worten kommt dieses einem Versuch gleich, ein paar
Jahrhunderte polnischer Geschichte auszustreichen und die
Bankerotterklärung der adeligen Anschauungen in Polen ein für
allemal zu statuieren.</p>
<p class="calibre3">Es ist kein Wunder, daß bei dieser um sich greifenden Stimmung
der Bauer in der neuesten polnischen Literatur eine ganz
exzeptionelle Rolle zu spielen beginnt.</p>
<p class="calibre3">Unter den vielen modernen polnischen Dichtungen, die der
Schilderung bäuerlicher Motive gewidmet sind, verdienen vor allem
drei hervorgehoben zu werden: »Die Hochzeit«, das schon vorhin
erwähnte Märchendrama Stanislaw Wyspianskis, »Herr Balcer in
Brasilien«, das Epos von der polnischen Auswanderung nach Amerika
von Maria Konopnicka und die »Bauern«, das große realistische
Gemälde des polnischen Dorflebens von W. S. Reymont. Die feinen,
unter einer wundersam gesponnenen Symbolik verborgenen Fäden der in
einen nationalen Tanzrhythmus vorübergleitenden kaleidoskopartigen
Wyspianskischen Hochzeitsdichtung, die Gegenwärtiges und
Zukünftiges zu verbinden trachten, sind allerdings nicht leicht für
einen Ausländer herauszuspüren. Ist es aber einem darum zu tun,
eine Ahnung polnischer Entwickelungsmöglichkeiten zu gewinnen, so
sind gerade die Werke Wyspianskis neben denen von Stefan Zeromski
die geeignetsten dazu. Maria Konopnickas Auswanderungsepos, das
wohl die vollendetste polnische epische Dichtung seit der Zeit der
großen Romantiker ist und in seinem Aufbau, seiner
Schilderungskunst, seiner Sprache unter die Meisterwerke der
Polnischen Literatur gezählt werden muß, ist in seiner Analyse der
Bauernpsyche viel zu sehr von sentimentalen Nebengefühlen
beeinflußt, um als das Buch des polnischen Bauerntums gelten zu
können; den Anspruch darauf darf einzig das große vierbändige
Prosaepos von Reymont erheben. In diesem Werk, in dem sich ein ganz
seltsam temperamentvoller Naturalismus und eine vortreffliche
Kenntnis <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page18" title="Ruktz/lac"></a> des
polnischen Bauernlebens vereinen, ist ein bis in alle Einzelheiten
wahres Gemälde des polnischen Dorflebens gegeben worden.</p>
<p class="calibre3">Reymonts Werk erschien gerade an der Schwelle außerordentlich
wichtiger, für die Entwickelung des östlichen Slawentums
entscheidender politischer Ereignisse – vor Rußlands Niederlage im
Japanischen Krieg und vor dem Beginn der großen revolutionären
Bewegung im russischen Reich. Es fixiert polnische Dorfzustände mit
einer Klarheit, in der sie augenblicklich nicht gesehen werden
können, es gibt uns einen Schlüssel zu den verworrenen Vorgängen,
die sich heute auf politischem und sozialem Gebiet in Polen
abspielen; und was vor allem wichtig ist, es führt uns den
polnischen Bauer, auf den heute das polnische Volk dermaßen erhöhte
politische Hoffnungen setzt, im unverfälschten und durch keinerlei
doktrinäre oder ideologische Gesichtspunkte »verschönten«
Rohzustand vor. Das ist die aufschlußgebende Bedeutung der
Reymontschen Dichtung.</p>
<p class="calibre3">Dabei ist natürlich nicht zu vergessen, daß es wirklich eine
Dichtung ist, die uns diese Einblicke in ein interessantes Neuland
verschafft. W. S. Reymont ist der berufene Epiker des polnischen
Dorfes. Er ist trotz seines fremdländisch klingenden Namens ein
Kind der fremden, kulturfernen Welt des polnischen Dorfes, in der
er seinen Lebenslauf mitten unter Gänsejungen und Viehhütern,
selbst die Herde seines Vaters, eines dem Bauernstande entwachsenen
Dorforganisten, hütend, seinen Lebenslauf beginnt, um allmählich
auf der sozialen Stufenleiter zur Stellung eines beliebten und
bekannten Dichters emporzusteigen, der schon seit Jahren einen
beträchtlichen Teil seiner Zeit in Paris, im Zentrum der modernen
Kultur, zu verleben pflegt. Reymont kennt das Dorf aus
unmittelbarer Nähe, bis in jede Einzelheit, bis in die geheimsten
Winkel und Falten der polnischen Bauernseele, bis zu den
Eigentümlichkeiten der polnischen Bauernsprache, er kennt es mit
der tiefen, instinktsicheren <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page19" title="Ruktz/gary"></a> Kenntnis eines Menschen, den die vertrautesten
Erinnerungen der Kindheit mit dem Milieu verbinden, der aus der
Fülle schöpft, und was besonders zu betonen ist, sich trotz der
bunten Lebensschicksale, die ihn allmählich dem polnischen Dorf
entführt haben, die ihn von der Tonbank eines städtischen Ladens
zum Wanderkarren eines fliegenden Provinztheaters, von dem
lärmenden Tagwerk eines Bahnarbeiters bis in die Stille einer
Klosterzell trieben, dennoch nie diesem Dorf entfremdet hat.</p>
<p class="calibre3">Reymont ist nicht nur der Schilderer des polnischen Dorfes, er
ist das zum sprachlichen Ausdruck gewordene bäuerlich-ländliche
Element, er ist das unmittelbarste rhythmische und seelische
Bekenntnis des polnischen Bauerntums. Das ist die Stärke und auch
zuweilen die Schwäche seines Werkes. Seine »Bauern« halten somit im
weitesten Sinne das, was der Titel verspricht/sie sind das Epos des
polnischen Dorfes. Das polnische Dorf, wie es in Russisch-Polen
vornehmlich zu finden ist (seine deutschen und österreichischen
Gegenstücke sind nur unmerkliche Abweichungen von diesem Typus),
ist trotz der Anekdote, die auch der dramatischen Spannung nicht
entbehrt, der eigentliche Romanheld. Das Dorf ist es, das den Kampf
um den gemeinsamen Waldbesitz mit dem Gutshof führt, dessen
einzelne Phasen den Hintergrund für die beiden Teile des Romans
bilden, das Dorf, das sich sein eigenes Recht in einem prachtvoll
gezeichneten, höchst dramatischen und zugleich humoristischen Kampf
im Walde verschafft und dafür die tragikomische Strafe einer
gemeinsamen Haft erleidet, das Dorf, das gegen Antek, den Sohn des
ersten Dorfpotentaten, des alten Matheus Boryna, vorgeht, als
dieser eigenwillig die Schranken der gemeinsamen Sitte zu
durchbrechen droht, das Dorf/das gegen die russische Regierung und
gegen die deutschen Kolonisten Stellung nimmt, das das aus
kommunistisch-demokratischen Lebensanschauungen erwachsene Prinzip
der gemeinsamen Abwehr und der gemeinsamen <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page20" title="Ruktz/gary"></a> Hilfe in der Not in eine Reihe von
Taten umsetzt, deren sozialpolitische Bedeutung nicht zu verkennen
ist und das eine grausame Lynchjustiz an einer Dorfhelena ausführt,
um die sich das Begehren des leicht erregbaren Blutes, das auch den
polnischen Bauer kennzeichnet, wie um die zweite Achse der Handlung
dreht.</p>
<p class="calibre3">Gegenüber dieser Macht der Gemeinde schrumpft die einzelne
Persönlichkeit zu einem fast nichtssagenden Schnörkel zusammen. Es
gibt natürlich Anführer, und die demokratisch-kommunistische
Dorfgemeinde bedarf ihrer noch mehr, als eine freie Verbindung von
selbständigen Bündlern, ein solcher Anführer ist bei Reymont der
alte Matheus Boryna, der reichste und angesehenste Bauer im Dorf.
Doch ist ihre Macht und ihr Einfluß im Dorf dermaßen durch den
Zusammenhang mit der Gemeinde bedingt, daß sie eigentlich mehr als
deren höchste Vertreter und Interpreten, nicht aber als einzeln für
sich dastehende Persönlichkeiten zur Geltung kommen.</p>
<p class="calibre3">Der zweite Ring, der die Einzelpersönlichkeiten mit eiserner
Gewalt umschließt und ihnen noch stärker den demokratischen Stempel
einer streng aufeinander angewiesenen Gemeinschaft aufdrückt, ist
die völlige Naturgebundenheit, in der sich die polnischen Bauern
befinden. Die echt slawische rückhaltslose Hingabe des Polen an die
Natur, seine Naturversunkenheit, die nicht einmal einen Versuch von
Auflehnung zeigt, die das Gefühl vertrauender Kindesliebe über den
Standpunkt der Empörung wider die Naturgewalten, den Kampf gegen
sie und ihre Überwindung stellt, trägt noch mehr dazu bei, die
Einzelpersönlichkeit im Dorfleben der Gemeinde verschwinden zu
lassen. Das Individuelle versinkt fast spurlos im Naturgeschehen,
wie ein einzelner Wanderer in der Unendlichkeit der slawischen
Ebene, in der selbst Dörfer und Gehöfte belanglos erscheinen.</p>
<p class="calibre3">ES ist Reymont in seinen »Bauern« gelungen, einen <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page21" title="Ruktz/gary"></a> außerordentlich
eindringlichen Ausdruck dieser Naturgebundenheit des polnischen
Bauers zu geben, nicht nur durch den Aufbau seines vierteiligen
Romans im Rahmen der Aufeinanderfolge der vier Jahreszeiten
(Herbst/Winter, Frühling/Sommer), sondern auch durch die Gliederung
dieses Jahresringes. Die Handlung beginnt mit der Zeit der ersten
Aussaat, mit der nach der Ernte einsetzenden Vorbereitung künftiger
Erntemöglichkeiten, um über die Hemmungen des Winters hinaus, sich
zum Höhepunkt der Sommererntezeit zu steigern. So wird die
Jahreszeit mit allen ihren Forderungen und mit ihrem Geschehen zum
Grundmotiv der Handlung selbst. Wie eine allmächtige,
allbestimmende Gewalt steht die Natur hinter allen Vorgängen. Die
vortreffliche Zusammenschweißung beider ist eine besondere Stärke
der Reymontschen Dichtung.</p>
<p class="calibre3">Wir sehen den Herbst in seiner bunten Pracht der Reife
aufglühen; wie stille Bilder reihen sich die einzelnen Geschehnisse
aneinander. Kartoffelernte, Kohlschneiden, ein Septembersonntag im
Dorf mit Kirchenandacht und einem Tanzvergnügen, Herbstmarkt, eine
typische Abendzusammenkunft mit Kohlschälen, Allerseelen mit seinen
eigentümlichen Bräuchen, eine Bauernverlobung und Hochzeit ... In
diesen Rahmen ist eine Anekdote hineingespannt: die Geschichte
einer Nebenbuhlerschaft zwischen Vater und Sohn wegen einer
Dorfschönen. Mit einer prunkvollen Hochzeit, die alle Farben des
polnischen Dorfes in sich zu einer koloristisch und rhythmisch
außerordentlich reichen Schilderung vereinigt, schließt das bunte
Herbstbild. Wie ein Vorbote des schon nahen Winters mischt sich
zuletzt noch eine elegische Todesweise in die Festfreude des Dorfes
hinein, ganz wundersam mit seinen Kontrasten das hohe Fest des
Lebens durchwebend.</p>
<p class="calibre3">Der Winter bringt dramatische Töne. Das polnische Dorf in Eis
und Schnee wird sichtbar. Der Schankwirt, die jüdischen Händler und
der Müller halten ihre Erntezeit, <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page22" title="Ruktz/gary"></a> und aus dem todähnlichen Schlaf der Abwehr,
in dem das Dorf nach außenhin ganz befangen zu sein scheint, reckt
sich immer drohender, je näher es nach dem Vorfrühling zu geht, die
bange Sorge der Vorerntenot. Die ungesicherten, traurigen
Verhältnisse des polnischen Dorfproletariats treten in ihrer ganzen
Kraßheit vor unsere Augen. Sie kommen anschaulich in den
Schicksalen des reichen Hofbauernsohns Antek Boryna zur Geltung,
den der alte Vater von Haus und Hof gejagt hat und der nun mit Frau
und Kind in einer ärmlichen Kate alle Schrecknisse einer auf Gnade
und Ungnade den unerbittlichen Naturgewalten ausgelieferten
Existenz am eigenen Leibe erleben muß. Eine der prachtvollsten
Schilderungen dieser Art sind die Erlebnisse einer schwangeren,
verratenen Frau im Hochwald während eines Schneesturms beim
Reisigsammeln. Den scharfen Akzenten, die das Naturbild zeigt,
entspricht das sich verdüsternde Bild der Leidenschaften. Es werden
den Leser allerdings die verträumten Bilder der Weihnachtszeit, mit
all ihrer innigen Feierlichkeit mit ihren alten Sitten und
Bräuchen, die Spinnabende mit ihrer Lust am Fabulieren, ihrem
Mummenschanz, Rätselspielen usw. wie traute Ruhepausen hier und da
festhalten; aber der Gang der Handlung ist doch ein unerbittlich
ernster, zu einer Katastrophe hindrängender. Die Feindschaft
zwischen Vater und Sohn wächst nach der Hochzeit des alten Borynas,
die einen vorläufigen Sieg des Vaters bedeutet, während sich die
Liebesfäden zwischen Sohn und Stiefmutter immer enger zu einem
verhängnisvollen Ehebruch knüpfen. Der mißlungenen Rache des
Vaters, der den Sohn mit der Frau <span class="teletype">in
flagranti</span> in einem Heuschober überrascht, welchen er darauf
in Brand setzt, folgt der Waldkampf des ganzen Dorfes gegen den
Gutshof, währenddessen sich dem Sohn eine Gelegenheit zur blutigen
Vergeltung bietet, die er jedoch im letzten Augenblick nicht
ausführt. Wie ein die Welt reinfegender Märzsturm schließt der
hochdramatische Waldkampf das strenge Winterbild ab. <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page23" title="Ruktz/gary"></a> Das allmähliche Werden des
Frühlings, die drängende Triebkraft der erwachenden Fruchtbarkeit
wirken besonders eindrucksvoll durch die Hemmungen, in denen sich
das Dorf nach dem verhängnisvollen Waldkampf befindet. Es kehren
schon die Bettler wie Wandervögel von ihren Winterstreifzügen in
das heimatliche Dorf zurück, wo man jetzt jeglichen Zuwachs an
Arbeitskräften mit Freuden begrüßt, aber sie finden totenhafte
Ruhe. Die Erde ist erwacht, alles sprießt und wächst, der Acker
wartet auf den aufwühlenden Pflug und auf den befruchtenden Samen,
aber die Menschen sitzen im Gefängnis. Es ist Reymont durch diese
Kontraste gelungen, eine ganz eigenartige Frühlingsstimmung zu
erzeugen, die der wirklichen Lage im polnischen Dorf viel näher
kommt, als alle dithyrambischen Frühlingshymnen der früheren
Schilderer des polnischen Bauerntums. Das Frühlingserwachen auf dem
polnischen Dorf ist ein schwerer, krisenhafter Vorgang. Die bittere
Not steht an der Pforte zu neuen Erfüllungen. Das Wort
»Vorerntezeit« ist im Polnischen gleichbedeutend mit dem Begriff
schwerer Entbehrungen. Die schwierigen ökonomischen Verhältnisse
erlauben einem recht ansehnlichen Bruchteil der Landleute nicht,
von dem Ertrag der Ernte ein ganzes Jahr zu leben, und da dabei die
Gelegenheitsverdienste gering und sehr unsicher sind, so stellt
sich in der Frühlingszeit meist schon eine recht empfindliche Not
ein, die in einzelnen Teilen des russischen Reiches selbst die Form
von chronischen Hungersnöten annimmt. Dagegen sind die Hilfsmittel
des einzelnen und selbst der Regierung machtlos. Darum ist auch das
Aufkommen des kommunistisch-demokratischen Prinzips der gemeinsamen
Selbsthilfe, das von nun an in der Handlung des Reymontschen Romans
eine entschieden führende Rolle übernimmt, außerordentlich typisch
für slawisches Empfinden und slawische Entwickelungsmöglichkeiten.
Es ist das alte unter den Piasten in Polen zum politischen Ausdruck
gekommene Prinzip des demokratischen Bauernstaates, auf <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page24" title="Ruktz/gary"></a> das sich auch die
Hoffnung der modernen polnischen Politiker stützen.</p>
<p class="calibre3">Die Handlung nimmt nun ganz beträchtlich an Breite zu, sie wird
wie ein Strom, der seine Schnellen überwunden hat und sich nun, zu
einer gewaltigen Wassermenge angewachsen, seiner Mündung
entgegenwälzt. Darin ist der Roman für slawisches Empfinden
außerordentlich bezeichnend, obgleich er für westeuropäische und
vor allem für germanische Begriffe einen empfindlichen Mangel an
Straffheit aufweist. Er zerfließt fast zur Formlosigkeit, und doch
ist gerade dieses Zerfließen ein notwendiger Ausfluß slawischer
Wesensart.</p>
<p class="calibre3">Um der Entwicklung der Handlung das rechte Verständnis
entgegenzubringen, wird es zunächst von Wichtigkeit sein, zwei
Schwerpunkte zu beachten: Die Natur und die Macht der Dorfgemeinde.
Zwischen beiden schlängelt sich die immer stärker symbolisch
anmutende Romananekdote.</p>
<p class="calibre3">Das Aufblühen der guten Jahreszeit umfängt allmählich wie mit
einem Zauber die ganze Gefühlsatmosphäre des Dorfes. Ein schwerer,
berauschender Erdgeruch durchzieht das Buch, legt sich betäubend
und verführerisch auf die Sinne, verdichtet sich zu Visionen, die
an altslawische Naturgottheiten erinnern und zuweilen selbst etwas
von der naturinnigen Lieblichkeit Gauginscher Erlebnisse haben.
Bezeichnend dafür ist besonders der Grundton der Schilderung einer
Maiandacht auf einem Dorfkirchhof. Dieses immer mächtiger
anwachsende Naturmotiv findet seine symbolisch-menschliche
Ergänzung in der Schilderung des Todes des alten, im Waldkampfe
schwer verletzten Boryna, der in einer mondhellen Mainacht, am
Vorabend der angesagten Rückkehr seines Sohnes aus dem
Untersuchungsgefängnis, bloß im Hemd und barfuß mit säend-segnender
Gebärde über sein Ackerland schreitet, oder in dem ersten Gang des
wiedergekehrten Antek Boryna, der mit seinem kleinen Söhnchen auf
dem Arm durch die ernteverheißenden Felder <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page25" title="Ruktz/gary"></a> geht, von denen er wie ein Erbe
endloser, erdgebundener Geschlechter und als Vorfahr neuer
endloser, erdgebundener Generationen Besitz ergreift.</p>
<p class="calibre3">Auch das kommunistisch-demokratische Element der polnischen
Dorfgemeinde nimmt im Entwicklungsgang der Handlung einen immer
wichtigeren Platz ein. Die Einzelschicksale werden im Vergleich zu
dieser immer klarer hervortretenden Gesamtpersönlichkeit in den
Hintergrund gedrängt. Auch das ist durchaus slawisch empfunden. Von
diesem Gesichtspunkt aus zeigt der Roman ein ganz besonderes
einheitliches Gefüge, das ihm sonst fehlen würde, wenn man
lediglich die Entwicklung der Einzelschicksale betrachten wollte.
Außer der ganzen im übrigen verständlichen, wenn auch
ungewöhnlichen Wendung bleibt in der Entwickelung des Reymontschen
Romans noch die ziemlich breit ausgesponnene Jagna-Episode zu
erklären, die recht kräftig in den Schluß hineinklingt.</p>
<p class="calibre3">Vielen wird die Gestalt dieses vielumworbenen Dorfmädchens, der
blondhaarigen und blauäugigen Dorfhelena, die den Sohn liebt, den
Vater heiratet, mit diesem Sohn dann zur Ehebrecherin wird, mit dem
Dorfschulzen eine skandalöse Liebschaft hat, von einem früheren
Liebhaber, noch ehe sie Witwe geworden, wieder umschwärmt wird und
sich selbst in den Organistensohn Jascho, einen angehenden
Priester, verliebt, den sie mit ihrer sinnlosen Leidenschaft
verfolgt, um schließlich vom ganzen Dorf gelyncht zu werden, als
eine rätselhafte, unverständliche und vielleicht selbst
unglaubwürdige Gestalt vorkommen. Der Dichter selbst
charakterisiert sie einmal im ersten Teil seines Bauernepos mit
folgenden Worten (s. S. 153):</p>
<p class="calibre3">»... Denn wie die heilige Erde war Jagnas Seele/ ganz wie diese
Erde; sie lag in Tiefen, die niemand erkennen konnte, in der
Wirrnis schlaftrunkener Träume/ riesig und unbewußt, mächtig, aber
ohne Willen, ohne Wollen, ohne Wünsche, totenstarr und dennoch
unsterblich, <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page26" title="Ruktz/gary"></a>
und wie diese Erde nahm sie jeder Wind, umhüllte sie, schaukelte
sie und trug sie dahin, wo er wollte ... sie war wie die Erde, die
die warme Sonne zur Frühjahrszeit weckt, mit Leben befruchtet, mit
dem Feuerschauer des Verlangens, der Liebe erschüttert/und sie
gebiert, weil sie muß; sie lebt, singt, herrscht, schafft und
vernichtet, weil sie muß; sie ist, weil sie muß ... denn wie die
heilige Erde war Jagnas Seele/ganz wie diese Erde! ...«</p>
<p class="calibre3">Diese Schilderung ist jedoch nur ein flüchtiger Hinweis, wo wir
den Schlüssel zu Jagnas seltsamer Menschlichkeit zu suchen haben.
Erklärlich wird sie nur, wenn man das sinnliche Moment und seine
Bedeutung für die slawische Psyche begriffen hat. Es gibt
allerdings eine ganze Stufenleiter slawischer Sinnlichkeit von der
tschechischen prallen Lebensfreude bis zur tiefsinnigen russischen
Leidenschaftlichkeit, zwischen denen die polnische Heißblütigkeit
in der Mitte steht. Man darf nicht vergessen, daß die
Hauptgestalten des Reymontschen Romans, wie das die »Revue des deux
Mondes« in einem umfassenden Aufsatz über Reymont treffend bemerkt,
»eine unverkennbare symbolische Bedeutung haben, die sie doch nicht
hindert, in unseren Augen die Merkmale einer greifbaren
menschlichen Wahrhaftigkeit zu bewahren«. Eine solche symbolische
»tiefere« Bedeutung ist auch der Gestalt der Jagna beizulegen, die
neben dem alten Boryna, seinem Sohn Antek und dessen Frau Anna zu
den in diesem Sinne deutbaren Hauptgestalten des Romans gezählt
werden muß. Damit aber berühren wir das spezifisch
Slawisch-Polnische im Reymontschen Bauernroman.</p>
<p class="calibre3">Auf das demokratisch-kommunistische Gefühl als eine besondere
slawische sozialpolitisch formende Kraft habe ich bereits
hingewiesen, es ist der individualistisch-aristokratischen
Lebensanschauung des Germanen diametral entgegengesetzt, so wie die
kommunistische Dorfgemeinde der Urslawen der Gegenpol der
urgermanischen norwegischen <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page27" title="Ruktz/gary"></a> Höfe ist. Dem Slawen fehlt das Problematische des
Germanen. Seine Naturversunkenheit ist ekstatisch ohne Widerspruch,
er identifiziert sich mit der Natur, ohne den Zwiespalt zu fühlen,
der als Faustischer Zug durch alles Germanische geht. Dadurch hat
er sich eine gewisse taufrische Naturinnigkeit bewahrt, die auch
heute noch als eine Stärke von den geschichtlich älteren Nationen
Europas empfunden wird, im Grunde aber seine Schwäche ist, denn nur
aus dem Zwiespalt mit der Natur entsteht die Naturüberwindung, nur
über die Abgründe des Problematischen kann man zur siegreichen
Selbstverewigung des monumentalen Stils gelangen. Der völlige
Mangel monumentaler Gestaltungsfähigkeit zeichnet den Slawen aus,
und wie sehr er ein Gegner des individualistischen
Sich-selbst-formens, Sich-selbst-wiederfindens im Weltgeschehen
ist, dafür liefert einer der größten und echtesten Interpreten der
slawischen Seele, Fedor Dostojewski, mit seinem fanatischen Haß des
individualistischen Prinzips einen recht beredten Ausdruck. Wir
brauchen solchen Werken nur die alten isländischen Sagas
entgegenzustellen, in denen sich das germanische Empfinden in
seiner reinsten Gestalt, denn noch frei vom Einfluß des
Christentums, monumental auszusprechen vermocht hat, um zu sehen,
daß es sich hier um prinzipielle und nicht durch geschichtliche
Vermengungen erst gewordene Unterschiede handelt.</p>
<p class="calibre3">Würde der christlichen Idee der Menschenverbrüderung eine
sozialpolitische Verwirklichung im europäischen Völkerleben
beschieden sein, dann könnte allerdings die
kommunistisch-demokratische Gesellschaftsordnung, an der die Slawen
seit jeher festzuhalten versucht haben, eine weltgeschichtliche
Bedeutung gewinnen, auch die Gedanken der modernen Propheten des
Demokratismus (ich möchte hier besonders auf die Wendung hinweisen,
die der Sozialismus im heutigen England genommen hat/Näheres
darüber ist in dem vortrefflichen Werk des Schweben Gustaf F.
Steffen <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page28" title="Ruktz/gary"></a> »Die
Demokratie in England« zu finden) haben merkwürdigerweise gewisse
Berührungspunkte mit diesen kommunistisch-demokratischen Begriffen
des Slawentums. Da wir aber das Wertvollste, das wir bis jetzt an
Kulturerrungenschaften besitzen, unverkennbar nicht dem
empfangenden und interpretierenden, sondern dem gestaltenden und
eigenwillig prägenden aristokratisch-individualistischen Prinzip zu
verdanken haben, so ist die slawische »Seele« als ein Element zu
betrachten, daß nicht ohne folgenschwere Einwirkungen dem
Kulturbesitz beigemengt werden kann.</p>
<p class="calibre3">Was dem Slawen an Gestaltungsfähigkeit abgeht, das ersetzt er
durch die Stärke des Empfindens. Diese Stärke des Empfindens finden
wir auch im hohen Maße in Reymonts »Bauern«. Die Musik der Sprache,
die Farbe, der Rhythmus sind oft von einem beträchtlichen Schwung,
seine Vergleiche schillern und funkeln wie im grellsten
Sonnenlicht, seine Schilderungen brechen oft in ekstatischer
Seligkeit wie sprudelnde Lyrismen hervor, selbst sein
Naturalismus/denn Reymont trachtet die Seele und das Leben des
polnischen Dorfes von außenher mit einer bis ins Kleinste gehenden
Wahrheitstreue zu beschreiben/ist ganz ungewöhnlich temperamentvoll
und bildet für die Ausdrucksmöglichkeiten naturalistischer
Darstellungskunst zuweilen eine ganz neue Bereicherung. Beispiele
für Reymonts ekstatisch-naturalistische Art zu schreiben, ließen
sich leicht häufen, es möge hier nur eine besonders
charakteristische Schilderung polnischer Nationaltänze folgen, wie
sie auf Borynas Hochzeit getanzt wurden (s. Bd. 1, Herbst, S.
285):</p>
<p class="calibre3">»Und sie tanzten!</p>
<p class="calibre3">... Die zappelnden, schäkernden Krakowiaks mit der abgerissenen,
klirrenden Melodie, die wie mit Ziernäglein beschlagene Gürtel mit
tanzfrohen Liedlein ausgeputzt war; die Krakowiaks voll Lachen und
Mutwillen, voll fröhlichen Sangs und üppiger starker, kecker Jugend
und zugleich voll <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page29" title="Ruktz/gary"></a> lustiger Possen, voll Haschen und voll Greifen
und voll Glut des jungen liebeshungrigen Blutes. Hei!</p>
<p class="calibre3">... Mazurkas, langgedehnt wie Feldraine, breitgestreckt wie die
Mathiasbirnbäume, rauschend, und wie unabsehbare Ebenen so breit,
voll Schwergewicht und schlank aufstrebend, sehnsüchtig und
verwegen, gleitend und dräuend gepackt, würdevoll und
draufgängerisch und steifnackig dazu, wie jene Mannsleute, die zu
einem Haufen zusammengeschart, wie ein Wald aufragend, sich in den
Tanz werfen mit Juchzern und solcher Macht, als ob es zu hundert
gegen Tausende angehen sollte/und wenn man dabei die ganze Welt
zerreißen, verprügeln, zerstampfen, zu Splittern zertrampeln, auf
den Absätzen auseinandertragen müßte und selbst zugrunde gehen, um
dann noch nach dem Tode zu tanzen, mit den Hacken aufzutrampeln und
forsch auf mazurische Art aufzujuchzen: »Da-dana!«</p>
<p class="calibre3">... Und mächtige Obereks tanzten sie, ruckweise Springetänze,
schwindelnde, tolle, rasende, herausfordernde und wehmütige,
sengende und versonnene, mit Klageliedern durchwobene, im Siedetakt
des feurigen Blutes pulsende, und doch voll Güte und Liebe,
plötzlich wie eine Hagelwolke niedersausende, und voll herzlicher
Stimmen, voll himmelblauer Blicke, voll lenzverheißender Lüfte,
voll düfteschwangeren Zweigerauschens, das aus blütenschweren
Obstgärten kommt/Tänze, die wie jene sangerfüllten Frühlingsfelder
sind, Tänze, wo auch die Tränen noch durch Lachen fließen, und das
Herz Freudelieder singt, und die Seele sich sehnsüchtig losreißt,
den fernen Weiten, den entlegenen Wäldern entgegen und in die große
Welt hinausfliegt, ahnender Träume voll, vor sich hinsingend: »Oj,
Da-dana!«</p>
<p class="calibre3">Solche Schilderungen haben ihren besonderen stark anregenden
Reiz, doch sind sie zu eigenwillig, zu selbstherrlich, um sich
einem großen Ganzen unterzuordnen, sie zersprengen den Rahmen der
Form, und in großen Zusammenhängen <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page30" title="lac/gary"></a> bilden sich aus ihnen aneinandergereihte
Bilder, aber nicht ein einheitlicher, emporstrebender, einen
einzigen Willen monumental ausdrückender Bau/das ist der breite
slawische Strom, der ungebändigt, elementar sich durch eine weite
Ebene ergießt!</p>
<p class="calibre3">Diese Intensität des Empfindens setzt eine starke sinnliche
Erregbarkeit voraus. Sie ist ein Kennzeichen aller Slawen. Bei den
Polen ist sie das »heiße polnische Blut«, dessen explosive
Eigenschaften allgemein bekannt sind. Der Pole ist immer geladen,
in seiner Seele brennt ein unstillbares Flackerfeuer, das immer
wieder zu Ausbrüchen drängt, seine Bewegungen sind rasch, er braust
leicht auf und gerät schnell in den Zustand sinnloser Wut, oder
seliger Verzückung. Man findet bei Reymont Worte wie: kochen,
sieden, aufflammen, brodeln, aufeinander zuspringen, fliegen (für
laufen), ausbrechen, als die gewöhnlichen Charakteristika für
Empfinden und Bewegung/Ausdrücke, die in der wörtlichen Übersetzung
ins Deutsche als Übertriebenheiten wirken würden und dennoch im
Original durchaus echt sind. Wer sich überzeugen will, daß diese
Eigenschaft im Stil der meisten polnischen Dichter und
Schriftsteller als charakteristisches Merkmal zu finden ist, der
prüfe aufmerksam ihre Werke; besonders leicht kann man diese
Eigentümlichkeit bei Stanislaw Przybyszewski und vor allem an
seinen, auch in deutscher Sprache erschienenen Romanen
beobachten.</p>
<p class="calibre3">Vortrefflich hat die erwähnte Charaktereigenschaft des Polen
einer der seinsinnigsten polnischen Maler der Neuzeit, Jacek
Malczewski in einem Bild, das »Vor dem Sturm« benannt ist,
versinnbildlicht. Malczewski ist ein Künstler, der in seltsam
kühnen Bildern eine wundersam tiefe Symbolik zu geben weiß. Ein
polnischer Bauernjunge sitzt mit gesenktem, struppigem Kopf auf
einer Blumenwiese, die ganze Haltung hat etwas Lässig-Träges,
Hingebendes, aber der Ausdruck der eigentümlich stieren Augen und
des sinnlich geschwellten <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page31" title="lac/lesdochmal"></a> Mundes ist gespannt. Er horcht auf das
lockende Flüstern einer neben ihm einschmeichelnd hingekauerten
Phantasiegestalt, eines Weibes mit einem unförmlich raubtierhaften
Tigerleib, riesigen Hintertatzen und spitzen Bussardflügeln, die
aus den Schultern herauswachsen; eine ihrer Krallenhände hat sie an
seine Hand geschmiegt und sieht auf ihre sichere Beute mit
siegestrunkenem Lachen herab. Im Hintergrund, über ärmlichen, im
kahlen Feld dastehenden Katen, hängt schwarz eine brauende
Gewitterwolke ... Die Stimmung »Vor dem Gewitter« ist der normale
Zustand des polnischen Temperaments, das jeden Augenblick imstande
ist, auszubrechen und in einem wilden Elan durchzugehen.</p>
<p class="calibre3">Nun erst wird es uns möglich, Jagnas Gestalt aus Reymonts Roman
richtig zu verstehen. Jagna ist, wenn man die symbolischen Züge
zusammenfaßt, die ihrer durchaus echt und wahr dargestellten
Menschlichkeit entnommen werden könnten, die naturgebundene,
explosive, polnische Leidenschaftlichkeit. Sinnliche Erregbarkeit
und Naturversunkenheit schließen sich hier zu einem festen Ring
zusammen und bilden ein eigentümliches Gleichgewicht. Darum wirkt
ihre Erscheinung trotz ihrer vielfachen Liebesabenteuer doch nicht
lüstern, und ihre Bestrafung hat etwas von einem Martyrium. Man hat
das Gefühl, als leuchte über ihrem Haupt der blasse Lichtschein
einer fernen Ankunftsmöglichkeit, obgleich ihr Fuß über Schmutz und
Staub einer verdüsterten Gegenwart den verhängnisvollen Weg der
Verlorenen schreitet. Die tüchtige, energische, treue Anna, Antek
Borynas Frau, hat im Vergleich mit ihr etwas Engherziges, Ärmliches
und Graues.</p>
<p class="calibre3">Das Explosive des polnischen »heißen Blutes«, das sich im
polnischen Edelmann, etwas aus seiner Naturgebundenheit gelockert,
zu einem aristokratisch-revolutionären Element ausgebildet hat, ist
im polnischen Bauer noch naturgebunden geblieben und harrt als eine
unbekannte Möglichkeit <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page32" title="lac/DaoH"></a> /auf den Tag seiner Erlösung. Das Bild des
polnischen Dorfes, dieses für den nichtpolnischen Leser
literarischen Neulands, aus dem man jene Zukunft zum Teil
herauslesen könnte, hat Reymont mit einer bis in alle Einzelheiten
gehenden Deutlichkeit geschildert. Das ganze Bauernleben mit all
seinen Äußerungen von der Wiege bis zum Grabe, zu jeder Jahreszeit,
in jeglicher Lage und zu jeglicher Stunde des Tages wird uns
offenbar. Man kann, ohne zu übertreiben, behaupten, daß es kaum ein
Werk gibt, das ein wichtigeres Dokument zur Kenntnis des polnischen
Bauerntums genannt werden dürfte. Dieses poetische Dokument
erscheint in einem wichtigen Augenblick, und zwar in einer Zeit
tiefgehender Umgestaltungen im polnischen Bauernleben, am Vorabend
des Eintrittes des Bauern in nationalpolnische Geschichte. Das
Erwachen des sozialpolitischen Bewußtseins im polnischen Bauerntum
ist nämlich eine seit dem Beginn der russischen Revolution deutlich
hervortretende Erscheinung, die auch bereits ihren politischen
Ausdruck in der allpolnischen National-Demokratie gefunden hat.
Durch diese Wandlungen ist allerdings die Physiognomie des
polnischen Dorfes weniger deutlich erkennbar geworden und läßt sich
heute überhaupt nicht so feststellen, wie das Reymont gerade noch
im letzten Augenblick vor der beginnenden Verwandlung tun konnte.
Allerdings finden wir auch bei Reymont schon neben dem alten
Ausdruck des polnischen Dorflebens, das noch die Väter
repräsentieren, auch das Gären der neuen Zeit in den Söhnen. Die
erste Hälfte des Romans zeigt dementsprechend einen recht
merklichen Unterschied im Vergleich mit der zweiten. Gestalten, wie
der alte Dorfpatriarch Matheus Boryna, der derb-witzige Küster
Ambrosius, der unterwürfig-schlaue alte Bylica, der treue Knecht
Jakob Socha, die genügsame Bettlerin Agathe, die giftige Gusche und
die scheinheilige, habsüchtige Dominikbäuerin, Jagnas Mutter,
treten allmählich immer mehr in den Hintergrund, und mit ihnen
<a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page33" title="Ruktz/lac"></a> versinkt die
Zeit der rein vegetativen jahrhundertealten Epoche im polnischen
Bauernleben, um einem neuen Zeitalter Platz zu machen, dessen
Vertreter in Antek Boryna, Mathias Täubich, Jagnas Bruder Schymek
und Gschela, dem Bruder des Dorfschulzen, zu finden sind. Zwischen
beiden Welten steht Rochus, der an Tolstoische Gestalten erinnernde
Apostel-Bettler, der das nette Bewußtsein mit der Drangabe seiner
ganzen menschlichen Existenz predigt.</p>
<p class="calibre3">»Wegen mir braucht ihr keine Angst zu haben,« sagt er, als er
vor den ihm nachstellenden Gendarmen fliehen muß, »ein winziges
Stäubchen bin ich nur, ein Halm vom reichen Feld; nimmt man mich
und bringt mich ins Verderben, so schadet es nichts, denn es
bleiben noch viele solche und jeder ist bereit, auf gleiche Weise
sein Leben der Sache zu opfern ... Und kommt die Zeit, dann werden
Tausende von ihnen auftauchen; sie werden aus den Städten und aus
den Bauernhütten kommen und aus den Herrenhöfen auch, in einer
endlosen Reihe werden sie kommen und werden ihre Köpfe hingeben und
ihr Blut fließen lassen und werden einer nach dem anderen fallen,
sich wie Steine häufend, bis aus ihnen die heilige, ersehnte Kirche
aufgebaut wird ... Und ich sage es euch, daß sie erbaut wird und
bis in alle Ewigkeiten stehenbleibt, und keine böse Macht wird sie
überwinden können, denn sie wird aus Blutopfern und Liebe entstehen
...«</p>
<p class="calibre3">Was die Jungen von den Alten schon bei Reymont vorzüglich
unterscheidet, ist ein aggressiver Zug ihres Wesens, der sich nach
allen Richtungen hin bemerkbar macht. Die Alten sind in ihrer
Erdgebundenheit gleich Mensch gewordenen Ackerbaufunktionen, die
sich nur elementar betätigen, ohne über den engen Kreis ihres
Tagwerks hinauszugehen, sie handeln und empfinden kommunistisch aus
Instinkt, während die Jüngeren schon die Macht der sozialen
Zusammenhänge ahnen und bewußt an rasseeigentümliche Anfänge
anknüpfen.</p>
<p class="calibre3"><a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page34" title="lac/DaoH"></a> Über das
Ureigene der polnischen Volkspsyche bieten die vielfachen alten
Sitten und Bräuche, die von Reymont eingehend geschildert werden,
reichen Aufschluß. Vorchristliche Überlieferungen lassen sich
allerdings schwerlich in reinem Zustande finden, sie sind in einer
Menge von Bräuchen enthalten, die die Kirche ihren Zwecken
dienstbar gemacht hat und die darum, trotz ihres heidnischen
Ursprungs, wie z. B. gewisse Ostergebräuche, das Fest der
Kräuter-Muttergottes, die festlichen Umzüge mit Kirchenfahnen durch
die Felder und das Eingraben von geweihten Evangelien und Kränzlein
an den Feldgrenzen, der Kult der Toten am Allerseelentag u. v. a.
doch eine stark katholische Färbung erhalten haben. Mehr noch tritt
die besondere Eigenart der Volkspsyche dort hervor, wo das Volk die
Ranken einer selbständigen, naiven Dichtung um ein ihm geläufiges
Thema schlingt. Dafür liefern die Legenden und Geschichten, die
Rochus bei verschiedenen Gelegenheiten zum besten gibt, einen
interessanten Beweis (ein Meisterstück dieser Art ist die Legende
vom Herrn Jesus und Burek seinem Hündchen, die Rochus wahrend einer
Abendversammlung beim Kohlschälen erzählt). Auch die
charakteristischen Redensarten, Sprichwörter, Sinnsprüche,
Liedlein, Festgesänge, Bauernregeln und Rätselspiele, von denen das
Reymontsche Epos eine sehr reiche Auswahl bringt, bieten eine
außerordentlich ergiebige Ausbeute.</p>
<p class="calibre3">Als ein besonders klarer Spiegel der polnischen Volksseele muß
in Reymonts Roman die von köstlichen Ursprünglichkeiten reich
durchsetzte Sprache betrachtet werden. Der Dichter hat aus der
Bauernsprache heraus ein nicht nur in Dialogen, sondern auch in der
gesamten Ausdrucksweise zur Geltung kommendes und einzigartiges
Ausdrucksmittel neu geschaffen, das selbst im Satzbau zur Geltung
kommt, der der etwas schwerfälligen Gedankenarbeit und dem
lebhaften Gefühlsrhythmus des polnischen Bauernvolkes entspricht.
So sind die oft endlosen, von Leidenschaft fast erstickten <a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page35" title="lac/DaoH"></a> Perioden, die sich in
Überbietungen nicht genug tun können, und dann wiederum gewisse,
sich schwer dahinschleppende Rhythmen entstanden, in denen man
glaubt, das schlafbefangene Tasten erdgebundener Gedankengänge zu
hören. Also auch hier die starke, zu Explosionen hinneigende
Gefühlsspannung und die tief verankerte Naturversunkenheit. Soweit
diese Eigentümlichkeiten übertragbar waren, sind sie in der
vorliegenden Verdeutschung wiedergegeben worden.</p>
<p class="right"><span class="spaced">Jean Paul d'Ardeschah</span>
<a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" id="page36" title="lac/DaoH"></a></p>
<ol class="calibre7"><li class="calibre8"><a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" href="#/Text/#text1" id="foot1"><span class="pcalibre5 pcalibre4 footnote">Pflüger.</span></a></li>
<li class="calibre8"><a class="pcalibre3 pcalibre1 pcalibre2 pcalibre" href="#/Text/#text2" id="foot2"><span class="pcalibre5 pcalibre4 footnote">Im Jahre 1000 wurden die ersten drei
polnischen Bistümer in Kolberg, Breslau und Krakau unter der
erzbischöflichen Oberhoheit des Erzbischofs von Gnesen von
Magdeburg losgetrennt.</span></a></li>
</ol>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0002.vue?d2bc22d8